import { UpperCasePipe } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { DvButtonComponent, DvTooltipDirective } from '@devinco/ui/dds';
import { Entity } from '@shared/constants/entity';
import { TranslateModule } from '@ngx-translate/core';
import { OnCallStatus } from '@pages/on-call-page/constants';
import { OnCallDto } from '@pages/on-call-page/interfaces';
import { OnCallGridService } from '@pages/on-call-page/components/on-call-grid/services/on-call-grid.service';
import { LocationOnCallStatusIcon } from '@shared/components/timed-marker-map-menu-panel/constants';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import DateUtils from '@shared/utils/date.utils';
import { ColDef } from 'ag-grid-community';
import { map, switchMap, take } from 'rxjs';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';

@Component({
  imports: [
    TranslateModule,
    DvButtonComponent,
    UpperCasePipe,
    DvTooltipDirective,
  ],
  selector: RegistryComponentNames.LocationPopupContent,
  templateUrl: './location-popup-content.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./location-popup-content.component.scss'],
})
export class LocationPopupContentComponent implements OnInit {
  private api = inject(RestApiService);
  private onCallGridService = inject(OnCallGridService);
  private rolesAndPermissionsService = inject(UserRolesAndPermissionsService);

  @Input() userName: string;
  @Input() location: string;
  @Input() onCallStatus: OnCallStatus;
  @Input() mobile: string;
  @Input() properties: string;
  @Input() department: string;
  @Input() callOutCount: string;
  @Input() userId: string;
  @Input() onCallSiteId: string;
  @Input() lastCallOutTime: string;
  @Input() onCallId: string;
  @Input() canBeCalledOut: '1' | '0';

  onCallStatusIconUrl: typeof LocationOnCallStatusIcon =
    LocationOnCallStatusIcon;
  onCallStatusEnum: typeof OnCallStatus = OnCallStatus;
  isCalledOut = false;
  hasOnCallEdit = false;

  ngOnInit(): void {
    this.isCalledOut = this.canBeCalledOut === '0';

    if (this.lastCallOutTime) {
      this.lastCallOutTime = DateUtils.convertDateTimeToNO(
        new Date(this.lastCallOutTime)
      );
    }
    this.rolesAndPermissionsService
      .hasPermissions([RolesAndPermissions.StandbyEdit])
      .pipe(take(1))
      .subscribe(hasPermission => {
        this.hasOnCallEdit = hasPermission;
      });
  }

  callOut(): void {
    this.api
      .putCustom(`oncall/deploy/${this.userId}/${this.onCallSiteId}`)
      .pipe(
        switchMap(() => {
          const fields = this.onCallGridService.gridApi
            .getColumnDefs()
            ?.map((colDef: ColDef<OnCallDto>) => colDef.field!)
            .filter((field: string) => field !== 'select') as string[];

          return this.api
            .getData({
              entity: Entity.OnCall,
              fields,
              filterModel: {
                ID: {
                  filterType: 'text',
                  type: 'equals',
                  filter: this.onCallId,
                },
              },
            })
            .pipe(map(resp => resp.data[0] as OnCallDto));
        }),
        take(1)
      )
      .subscribe(locationData => {
        this.isCalledOut = true;
        const updatedNode = this.onCallGridService.gridApi.applyTransaction({
          update: [locationData],
        })?.update[0];
        if (updatedNode) {
          // forcely refreshing whole node to update custom cell renderer values
          this.onCallGridService.gridApi.refreshCells({
            rowNodes: [updatedNode],
            force: true,
          });
        }
      });
  }
}
